import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/app/Header";
import { api } from "../lib/api";
import {
  Calendar, MapPin, Sparkles, Loader2, Music, ArrowLeft,
  ExternalLink, CheckCircle2, AlertTriangle, Footprints, Car, Sun,
  Tent, Star,
} from "lucide-react";
import SaveFestivalButton from "../components/app/SaveFestivalButton";
import FestivalPackingListCard from "../components/app/FestivalPackingListCard";
import { useSeo } from "../lib/useSeo";

/**
 * /festivals/:slug — Single-festival detail page.
 *
 * Hero with festival info + countdown, then a tiered list of nearby
 * campsites — walking/shuttle distance < 10 mi, short drive 10-25 mi,
 * day-trip 25-40 mi. Each site carries an audience-fit verdict so a
 * family booking near Download sees "caution" on family-quiet sites,
 * and an adults-only booker near Glasto sees "great" on adults-only sites.
 */

const AUDIENCE_PILLS = {
  family: { label: "Family-friendly festival", color: "bg-[#16a34a] text-white" },
  adults: { label: "Adults / 18+ festival", color: "bg-[#9333ea] text-white" },
  rowdy: { label: "Late-night festival", color: "bg-[#dc2626] text-white" },
  boutique: { label: "Boutique festival", color: "bg-[#0891b2] text-white" },
  mixed: { label: "All-ages festival", color: "bg-[#0e0e0e] text-white" },
};

const BUCKET_META = {
  walking_shuttle: {
    title: "Walking or shuttle distance",
    subtitle: "Under 10 miles — leave the car at camp.",
    Icon: Footprints,
    color: "#16a34a",
  },
  short_drive: {
    title: "Short drive",
    subtitle: "10–25 miles — quick taxi or designated driver.",
    Icon: Car,
    color: "#ca8a04",
  },
  day_trip: {
    title: "Day-trip distance",
    subtitle: "25–40 miles — best if you're combining the festival with a holiday.",
    Icon: Sun,
    color: "#0891b2",
  },
};

function FitBadge({ fit, reason }) {
  if (fit === "great") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#16a34a] text-white px-2 py-0.5 text-[10px] mono uppercase tracking-wider" title={reason}>
        <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} /> Great fit
      </span>
    );
  }
  if (fit === "caution") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#ca8a04] text-white px-2 py-0.5 text-[10px] mono uppercase tracking-wider" title={reason}>
        <AlertTriangle className="w-3 h-3" strokeWidth={2.4} /> Check first
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#0e0e0e] text-white px-2 py-0.5 text-[10px] mono uppercase tracking-wider" title={reason}>
      <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} /> Good fit
    </span>
  );
}

function SiteCard({ site, idx }) {
  return (
    <Link
      to={`/campsites/${site.campsite_id}`}
      className="group block border hairline bg-white hover:shadow-[0_2px_0_0_#0e0e0e] hover:-translate-y-0.5 transition-all"
      data-testid={`festival-site-${idx}`}
    >
      {site.image ? (
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={site.image} alt={site.name} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {site.is_direct_booking && (
            <span className="absolute top-2 left-2 bg-[#ff4500] text-white px-2 py-0.5 text-[10px] mono uppercase tracking-wider shadow-[0_1px_0_0_#0e0e0e]">
              Book direct
            </span>
          )}
          <span className="absolute top-2 right-2 bg-white/95 text-[#0e0e0e] px-2 py-0.5 text-[10px] mono uppercase tracking-wider shadow-[0_1px_0_0_#0e0e0e]">
            {site.distance_miles} mi
          </span>
        </div>
      ) : (
        <div className="aspect-[4/3] bg-[#fafafa] border-b hairline flex items-center justify-center text-[#888]">
          <Tent className="w-8 h-8" strokeWidth={1.5} />
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <FitBadge fit={site.fit} reason={site.fit_reason} />
          {typeof site.rating === "number" && site.rating > 0 && (
            <span className="inline-flex items-center gap-0.5 text-xs mono text-[#666]">
              <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" /> {site.rating.toFixed(1)}
            </span>
          )}
        </div>
        <div className="font-display text-base leading-tight truncate group-hover:text-[#ff4500]">
          {site.name}
        </div>
        <div className="text-[11px] text-[#666] leading-snug mt-1">
          {site.fit_reason}
        </div>
      </div>
    </Link>
  );
}

function Bucket({ kind, sites }) {
  if (!sites || sites.length === 0) return null;
  const meta = BUCKET_META[kind];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <section className="mb-10" data-testid={`festival-bucket-${kind}`}>
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 text-white" style={{ background: meta.color }}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
        <h3 className="font-display text-2xl tracking-tight">{meta.title}</h3>
        <span className="text-xs mono text-[#666] uppercase tracking-wider">
          {sites.length} site{sites.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="text-sm text-[#555] mb-4 ml-[44px]">{meta.subtitle}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sites.map((s, i) => <SiteCard key={s.campsite_id} site={s} idx={`${kind}-${i}`} />)}
      </div>
    </section>
  );
}

export default function FestivalDetail() {
  const { slug } = useParams();
  const [festival, setFestival] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/festivals/${slug}`),
      api.get(`/festivals/${slug}/nearby-sites?radius_miles=40&limit=80`),
    ]).then(([f, n]) => {
      if (!alive) return;
      setFestival(f.data);
      setData(n.data);
    }).catch((e) => {
      if (alive) setError(e?.response?.status === 404 ? "Festival not found." : "Couldn't load this festival.");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // iter123 — Per-festival SEO meta + Schema.org Event markup.
  // Each festival page gets its own crawlable title/description so Google
  // can rank "<festival> camping" long-tail queries. Event schema unlocks
  // rich snippets (dates, location) in search results.
  const nearbyCount = Array.isArray(data?.sites) ? data.sites.length : 0;
  const seoTitle = festival
    ? `Camping near ${festival.name}${festival.location_label ? ` (${festival.location_label})` : ""} — CampCompass UK`
    : null;
  const seoDescription = festival
    ? `Find pitches within walking + shuttle + day-trip distance of ${festival.name}${festival.location_label ? ` at ${festival.location_label}` : ""}. ${nearbyCount} verified campsites mapped, no booking-fee middleman.`.slice(0, 320)
    : null;
  const seoJsonLd = festival ? {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": festival.name,
    "startDate": festival.start_date,
    "endDate": festival.end_date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": festival.location_label || festival.name,
      "geo": (festival.lat != null && festival.lng != null) ? {
        "@type": "GeoCoordinates",
        "latitude": festival.lat,
        "longitude": festival.lng,
      } : undefined,
      "address": festival.location_label || undefined,
    },
    "description": (festival.description || `${festival.name} festival — find every camper-friendly pitch nearby on CampCompass UK.`).slice(0, 500),
    "url": `https://camp-compass.uk/festivals/${festival.slug}`,
    "image": festival.image || undefined,
    "organizer": festival.organizer ? {
      "@type": "Organization",
      "name": festival.organizer,
      "url": festival.official_url || undefined,
    } : undefined,
  } : null;
  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: festival ? `/festivals/${festival.slug}` : null,
    jsonLd: seoJsonLd,
    jsonLdId: "festival",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="flex items-center justify-center py-32 text-[#555]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading festival…
        </div>
      </div>
    );
  }

  if (error || !festival) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl mb-3">Festival not found</h1>
          <p className="text-[#555] mb-6">{error}</p>
          <Link to="/festivals" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to festival list
          </Link>
        </div>
      </div>
    );
  }

  const pill = AUDIENCE_PILLS[festival.audience] || AUDIENCE_PILLS.mixed;
  const startLabel = festival.start_date ? new Date(festival.start_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="festival-detail">
      <Header active="festivals" />

      {/* HERO */}
      <div className="text-white" style={{ background: festival.hero_color || "#0e0e0e" }}>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/festivals" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider opacity-80 hover:opacity-100 mb-5">
            <ArrowLeft className="w-3 h-3" /> All festivals
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`${pill.color} px-2.5 py-1 text-[11px] mono uppercase tracking-wider`}>
              {pill.label}
            </span>
            {festival.days_until != null && festival.days_until >= 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-[#0e0e0e] px-2.5 py-1 text-[11px] mono uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> {festival.days_until === 0 ? "Today" : `${festival.days_until} days away`}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-4">
            {festival.name}
          </h1>
          <div className="space-y-1.5 text-sm sm:text-base text-white/90">
            <div className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {startLabel}
            </div>
            <div className="block">
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {festival.location_label}
              </span>
            </div>
          </div>
          {festival.description && (
            <p className="text-base text-white/85 leading-relaxed max-w-2xl mt-5">
              {festival.description}
            </p>
          )}
          {festival.official_url && (
            <a
              href={festival.official_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-5 text-sm mono text-white/80 hover:text-white uppercase tracking-wider"
              data-testid="festival-official-link"
            >
              Official site <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}

          {/* iter122 — Save festival + 14-day-before email reminder */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <SaveFestivalButton slug={festival.slug} festivalName={festival.name} />
            <span className="text-[11px] mono uppercase tracking-wider text-white/60">
              We'll email you 14 days before — packing checklist + closest pitches
            </span>
          </div>
        </div>
      </div>

      {/* NEARBY CAMPSITES */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        {/* iter122 — Packing list card (Pro perk + free preview). Mounted
            above the nearby-sites grid so a user arriving from the 14-day
            reminder email's "Preview your packing list" link sees it
            first; collapses by default otherwise. */}
        <FestivalPackingListCard slug={festival.slug} festivalName={festival.name} />

        <div className="mb-8">
          <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
            <Tent className="w-3 h-3" strokeWidth={2.4} /> Stay nearby
          </div>
          <h2 className="font-display text-3xl tracking-tight mb-2">
            {data?.total || 0} campsites within {data?.radius_miles || 40} miles
          </h2>
          <p className="text-[#555] max-w-2xl leading-relaxed">
            Every site is tagged for how well it suits a <strong>{pill.label.toLowerCase()}</strong> —
            so you don't book a family caravan park for Download or a rowdy site for the kids' first Latitude.
          </p>
        </div>

        {data?.total === 0 ? (
          <div className="text-center py-12 text-[#555] border hairline bg-white">
            No campsites mapped within 40 miles yet — try the
            <Link to="/browse" className="text-[#ff4500] underline mx-1">main browse page</Link>
            for a wider search.
          </div>
        ) : (
          <>
            <Bucket kind="walking_shuttle" sites={data?.buckets?.walking_shuttle} />
            <Bucket kind="short_drive" sites={data?.buckets?.short_drive} />
            <Bucket kind="day_trip" sites={data?.buckets?.day_trip} />
          </>
        )}
      </div>
    </div>
  );
}
